import { useQuery } from "react-query";
import { fetchData } from "../api/client";

export const useBotFlow = (usuario, idPai = 0, identificador = null) => {
  if (!usuario) {
    throw new Error("O parâmetro 'usuario' é obrigatório para carregar o BotFlow.");
  }

  return useQuery(
    ["botFlow", usuario, idPai, identificador],
    async () => {
      try {
        const params = new URLSearchParams({ usuario, limit: 100 });
        if (identificador) params.append("identificador", identificador);
        else if (idPai !== 0) params.append("id_pai", idPai);

        // console.log("🔍 Fetching bot flow with params:", params.toString());
        const response = await fetchData(`/botflow?${params.toString()}`);
        if (!response?.data) throw new Error("Resposta inválida da API");

        return response.data;
      } catch (error) {
        console.error("❌ Erro ao carregar o fluxo do bot:", error);
        throw new Error("Erro ao carregar o fluxo do bot. Tente novamente.");
      }
    },
    {
      staleTime: 10000,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
      retry: false,
      select: (data) => {
        if (!Array.isArray(data)) throw new Error("Formato inesperado dos dados do fluxo do bot.");

        const nodesMap = new Map();
        const nodes = data.map((item) => {
          let buttons = [];

          if (typeof item.botoes === "string") {
            buttons = item.botoes.split(",").map((btn, index) => ({
              id: `b${item.id}-${index + 1}`,
              label: btn.trim(),
            }));
          } else if (Array.isArray(item.botoes)) {
            buttons = item.botoes.map((btn, index) => ({
              id: btn.id || `b${item.id}-${index + 1}`,
              label: btn.label || `Botão ${index + 1}`,
            }));
          }

          const node = {
            id: item.id || `node-${item.id}`,
            type: "custom",
            position: { x: item.ordem * 200 || 100, y: 250 },
            data: { label: item.resposta ?? item.identificador },
            buttons: buttons,
            id_pai: item.id_pai,
          };

          nodesMap.set(item.id, node);
          return node;
        });

        // 🔹 Sanitização dos nós
        const sanitizedNodes = nodes.map((node, index) => ({
          id: node.id || `node-${index}`,
          type: "custom",
          position: node.position || { x: 100 * index, y: 100 },
          data: { label: node.data.label || `Nó ${index + 1}` },
        }));

        // 🔹 Criação das conexões (edges)
        const edges = data.flatMap((item) => {
          const parentNode = nodesMap.get(item.id_pai);
          if (!parentNode) return []; // Se não há pai, ignora
        
          return {
            id: `edge-${parentNode.id}-${item.id}`, // ID único para o edge
            source: String(parentNode.id), // ReactFlow exige strings
            target: String(item.id),       // ReactFlow exige strings
          };
        });

        // console.log("📌 Nós processados:", sanitizedNodes);
        // console.log("📌 Conexões processadas:", edges);

        return { nodes: sanitizedNodes, edges };
      },
    }
  );
};
